import { types as sdkTypes } from '../../util/sdkLoader';
import { userLocation } from '../../util/maps';
import config from '../../config';

const { LatLng: SDKLatLng, LatLngBounds: SDKLatLngBounds } = sdkTypes;

export const CURRENT_LOCATION_ID = 'current-location';

const GENERATED_BOUNDS_DEFAULT_DISTANCE = 500; // meters
// Distances for generated bounding boxes for different Mapbox place types
const PLACE_TYPE_BOUNDS_DISTANCES = {
  address: 500,
  country: 2000,
  region: 2000,
  postcode: 2000,
  district: 2000,
  place: 2000,
  locality: 2000,
  neighborhood: 2000,
  poi: 2000,
  'poi.landmark': 2000,
};

const locationBounds = (latlng, distance) => {
  if (!latlng) {
    return null;
  }

  const bounds = new window.mapboxgl.LngLat(latlng.lng, latlng.lat).toBounds(distance);
  return new SDKLatLngBounds(
    new SDKLatLng(bounds.getNorth(), bounds.getEast()),
    new SDKLatLng(bounds.getSouth(), bounds.getWest())
  );
};

const placeOrigin = prediction => {
  if (prediction && Array.isArray(prediction.center) && prediction.center.length === 2) {
    // Coordinates in Mapbox features are represented as [longitude, latitude].
    return new SDKLatLng(prediction.center[1], prediction.center[0]);
  }
  return null;
};

const placeBounds = prediction => {
  if (prediction) {
    if (Array.isArray(prediction.bbox) && prediction.bbox.length === 4) {
      // Bounds in Mapbox features are represented as [minX, minY, maxX, maxY]
      return new SDKLatLngBounds(
        new SDKLatLng(prediction.bbox[3], prediction.bbox[2]),
        new SDKLatLng(prediction.bbox[1], prediction.bbox[0])
      );
    } else {
      // If bounds are not available, generate them around the origin

      // Resolve bounds distance based on place type
      const placeType = Array.isArray(prediction.place_type) && prediction.place_type[0];

      const distance =
        (placeType && PLACE_TYPE_BOUNDS_DISTANCES[placeType]) || GENERATED_BOUNDS_DEFAULT_DISTANCE;

      return locationBounds(placeOrigin(prediction), distance);
    }
  }
  return null;
};

// Extract city from Mapbox context
const extractCity = prediction => {
  if (!prediction || !prediction.context) {
    // If no context, use place_name for place type predictions
    if (prediction && prediction.place_type && prediction.place_type.includes('place')) {
      return prediction.text || '';
    }
    return '';
  }

  // Look for place, locality, or district in context
  const cityContext = prediction.context.find(
    c => c.id.startsWith('place.') || c.id.startsWith('locality.') || c.id.startsWith('district.')
  );

  // If current prediction is a place type, use its text
  if (!cityContext && prediction.place_type && prediction.place_type.includes('place')) {
    return prediction.text || '';
  }

  return cityContext ? cityContext.text : '';
};

// Extract country name and code from Mapbox context
const extractCountry = prediction => {
  if (!prediction) {
    return { country: '', countryCode: '' };
  }

  // Check if country info is in properties (some place types)
  if (prediction.properties) {
    const countryCode = prediction.properties.short_code;
    if (countryCode && countryCode.length === 2) {
      // This is a country-level feature, not what we want for cities
      return { country: '', countryCode: '' };
    }
  }

  // Look for country in context array
  if (prediction.context) {
    const countryContext = prediction.context.find(c => c.id.startsWith('country.'));

    if (countryContext) {
      return {
        country: countryContext.text || '',
        countryCode: countryContext.short_code ? countryContext.short_code.toUpperCase() : '',
      };
    }
  }

  return { country: '', countryCode: '' };
};

export const GeocoderAttribution = () => null;

/**
 * A forward geocoding (place name -> coordinates) implementation
 * using the Mapbox Geocoding API.
 */
class GeocoderMapbox {
  getClient() {
    const libLoaded = typeof window !== 'undefined' && window.mapboxgl && window.mapboxSdk;
    if (!libLoaded) {
      throw new Error('Mapbox libraries are required for GeocoderMapbox');
    }
    if (!this._client) {
      this._client = window.mapboxSdk({
        accessToken: window.mapboxgl.accessToken,
      });
    }
    return this._client;
  }

  // Public API
  //

  /**
   * Search places with the given name.
   *
   * @param {String} search query for place names
   *
   * @return {Promise<{ search: String, predictions: Array<Object>}>}
   * results of the geocoding, should have the original search query
   * and an array of predictions. The format of the predictions is
   * only relevant for the `getPlaceDetails` function below.
   */
  getPlacePredictions(search, type) {
    const limitCountriesMaybe = config.maps.search.countryLimit
      ? { countries: config.maps.search.countryLimit }
      : {};

    return this.getClient()
      .geocoding.forwardGeocode({
        query: search,
        limit: 5,
        types: [type],
        ...limitCountriesMaybe,
        language: [config.locale],
      })
      .send()
      .then(response => {
        return {
          search,
          predictions: response.body.features,
        };
      });
  }

  /**
   * Get the ID of the given prediction.
   */
  getPredictionId(prediction) {
    return prediction.id;
  }

  /**
   * Get the address text of the given prediction.
   */
  getPredictionAddress(prediction) {
    if (prediction.predictionPlace) {
      // default prediction defined above
      return prediction.predictionPlace.address;
    }
    // prediction from Mapbox geocoding API
    return prediction.place_name;
  }

  /**
   * Fetch or read place details from the selected prediction.
   *
   * @param {Object} prediction selected prediction object
   *
   * @return {Promise<util.propTypes.place>} a place object
   */
  getPlaceDetails(prediction) {
    if (this.getPredictionId(prediction) === CURRENT_LOCATION_ID) {
      return userLocation().then(latlng => {
        return {
          address: '',
          origin: latlng,
          bounds: locationBounds(latlng, config.maps.search.currentLocationBoundsDistance),
        };
      });
    }

    if (prediction.predictionPlace) {
      return Promise.resolve(prediction.predictionPlace);
    }

    // Get localized address for display
    const localizedAddress = this.getPredictionAddress(prediction);
    const origin = placeOrigin(prediction);
    const bounds = placeBounds(prediction);

    // Check if the prediction itself is a place (city) type
    const isPlaceType = prediction.place_type && prediction.place_type.includes('place');

    // If we have coordinates and the prediction is NOT already a place/city,
    // fetch English version via reverse geocoding
    if (origin && origin.lat && origin.lng && !isPlaceType) {
      return this.getClient()
        .geocoding.reverseGeocode({
          query: [origin.lng, origin.lat],
          limit: 1,
          language: ['en'], // Always get English for structured data
        })
        .send()
        .then(response => {
          const englishFeature = response.body.features[0];

          if (englishFeature) {
            const { country, countryCode } = extractCountry(englishFeature);
            const city = extractCity(englishFeature);

            return {
              address: localizedAddress,
              origin,
              bounds,
              city,
              country,
              countryCode,
            };
          }

          // Fallback if reverse geocoding fails
          return {
            address: localizedAddress,
            origin,
            bounds,
            city: '',
            country: '',
            countryCode: '',
          };
        })
        .catch(error => {
          console.error('Reverse geocoding failed:', error);
          // Fallback on error
          return {
            address: localizedAddress,
            origin,
            bounds,
            city: '',
            country: '',
            countryCode: '',
          };
        });
    }

    // If the prediction is already a place/city, fetch English version by ID
    if (isPlaceType && prediction.id) {
      // Extract the place ID (e.g., "place.123456")
      return this.getClient()
        .geocoding.forwardGeocode({
          query: prediction.id,
          limit: 1,
          language: ['en'], // Get English version
        })
        .send()
        .then(response => {
          const englishFeature = response.body.features[0];

          if (englishFeature) {
            // Extract English country from the English feature response
            let { country, countryCode } = extractCountry(englishFeature);
            const city = englishFeature.text || prediction.text || '';

            // If country info is not in the English feature, try reverse geocoding
            if (!country || !countryCode) {
              console.log('Country not found in English feature, trying reverse geocoding');
              return this.getClient()
                .geocoding.reverseGeocode({
                  query: [origin.lng, origin.lat],
                  limit: 1,
                  language: ['en'],
                })
                .send()
                .then(reverseResponse => {
                  const reverseFeature = reverseResponse.body.features[0];
                  if (reverseFeature) {
                    const reverseCountry = extractCountry(reverseFeature);
                    return {
                      address: localizedAddress,
                      origin,
                      bounds,
                      city,
                      country: reverseCountry.country || country,
                      countryCode: reverseCountry.countryCode || countryCode,
                    };
                  }
                  return {
                    address: localizedAddress,
                    origin,
                    bounds,
                    city,
                    country,
                    countryCode,
                  };
                })
                .catch(err => {
                  console.error('Reverse geocoding for country failed:', err);
                  return {
                    address: localizedAddress,
                    origin,
                    bounds,
                    city,
                    country,
                    countryCode,
                  };
                });
            }

            return {
              address: localizedAddress,
              origin,
              bounds,
              city,
              country,
              countryCode,
            };
          }

          // Fallback to original prediction (will be localized but better than nothing)
          const { country, countryCode } = extractCountry(prediction);
          return {
            address: localizedAddress,
            origin,
            bounds,
            city: prediction.text || '',
            country,
            countryCode,
          };
        })
        .catch(error => {
          console.error('Forward geocoding for English failed:', error);
          // Fallback to original prediction (will be localized but better than nothing)
          const { country, countryCode } = extractCountry(prediction);
          return {
            address: localizedAddress,
            origin,
            bounds,
            city: prediction.text || '',
            country,
            countryCode,
          };
        });
    }

    // Fallback if no coordinates
    return Promise.resolve({
      address: localizedAddress,
      origin,
      bounds,
      city: '',
      country: '',
      countryCode: '',
    });
  }
}

export default GeocoderMapbox;
